import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Popup, Spinner, Button, Heading, Link } from "@ogt/ui";

import useFocusOnLoad from "../hooks/useFocusOnLoad";
import { useUnregisterFlow } from "../hooks/useUnregisterFlow";
import { createJourney } from "../utils/journeyHelpers";
import { getTravelPlanDetailsAPI as getTravelPlanDetails } from "../api/travelPlan";
import { cancelGroupTravelAPI as cancelGroupTravel } from "../api/unregister";
import JourneySummary from "../components/JourneySummary";

export default function UnregisterTravelSummary() {
  const h2Ref = useFocusOnLoad<HTMLHeadingElement>();
  const navigate = useNavigate();
  const hasRedirected = useRef(false);

  const [journeys, setJourneys] = useState<any[]>([]);
  const [showPopup, setShowPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasNoJourneyData, setHasNoJourneyData] = useState(false);

  const { saveUnregisterData, unregisterData } = useUnregisterFlow();

  const [searchParams] = useSearchParams();

  useEffect(() => {
    const referenceId = searchParams.get("ref");
    const editId = searchParams.get("id");

    if (!referenceId || !editId) {
      if (!hasRedirected.current) {
        hasRedirected.current = true;
        navigate("/avboka-resa");
      }

      setIsLoading(false);
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      setHasNoJourneyData(false);
      setJourneys([]);

      try {
        const result = await getTravelPlanDetails(referenceId, editId);

        const hasValidJourney = Boolean(
          result?.selectedJourney?.fromName &&
            result.ReferenceId &&
            result.EditId
        );

        if (!hasValidJourney) {
          setHasNoJourneyData(true);
          return;
        }

        saveUnregisterData({
          ReferenceId: result.ReferenceId,
          EditId: result.EditId,
          email: result.email,
        });

        const newJourneys = [
          createJourney(result.selectedJourney, "Resa dit", ""),
        ];

        if (result.selectedReturnJourney?.fromName) {
          newJourneys.push(
            createJourney(result.selectedReturnJourney, "Resa tillbaka", "")
          );
        }

        setJourneys(newJourneys);
      } catch (error) {
        console.error("Fel vid hämtning av gruppresa:", error);

        navigate("/avboka-resa-fel");
      } finally {
        setIsLoading(false);
      }
    };

    void fetchData();
  }, [searchParams, navigate, saveUnregisterData]);

  const handleConfirmUnregister = async () => {
    setShowPopup(false);

    if (!unregisterData?.ReferenceId || !unregisterData?.EditId) {
      navigate("/avboka-resa-fel");
      return;
    }

    try {
      await cancelGroupTravel(
        unregisterData.ReferenceId,
        unregisterData.EditId
      );

      saveUnregisterData({
        ReferenceId: unregisterData.ReferenceId,
        EditId: unregisterData.EditId,
        email: unregisterData.email ?? "",
      });

      navigate("/avboka-resa-gemomford");
    } catch (error) {
      console.error("Fel vid avanmälan av gruppresa:", error);

      navigate("/avboka-resa-fel");
    }
  };

  const canUnregister =
    !isLoading &&
    !hasNoJourneyData &&
    journeys.length > 0 &&
    Boolean(unregisterData?.ReferenceId) &&
    Boolean(unregisterData?.EditId);

  return (
    <div>
      <Box>
        <Heading className="mb-2" headingRef={h2Ref}>
          Avanmäl planerad gruppresa
        </Heading>

        <p className="mb-6">
          Här kan ni avanmäla er planerade gruppresa. Då avanmäler ni både resan
          dit och resan tillbaka. Om ni i stället vill ändra något eller bara
          avanmäla en av resorna kan ni ringa till oss på{" "}
          <Link underline="always" className="inline!" href="tel:+46771211010">
            0771-21 10 10
          </Link>
        </p>

        <Heading as="h3" className="font-semibold">
          Föranmälda gruppresor
        </Heading>

        <div className="flex sans-bold text-lg">
          <span className="font-semibold mr-1">Referensnummer: </span> {unregisterData?.ReferenceId ?? ""}
        </div>

        <hr className="my-6" />

        <div className="summary-section patient-summary mb-4 border-0 pb-0">
          <div className="summary-data-wrapper flex flex-col">
            {isLoading ? (
              <Spinner />
            ) : hasNoJourneyData || journeys.length === 0 ? (
              <p>
                Vi kunde tyvärr inte läsa in någon resa just nu. Vänligen försök
                igen senare eller kontakta kundservice.
              </p>
            ) : (
              journeys.map((journey, index) => (
                <JourneySummary
                  key={index}
                  journey={journey}
                  isReturnTravel={journey.Headline === "Resa tillbaka"}
                  showEditButton={false}
                  showRemoveButton={false}
                />
              ))
            )}
          </div>
        </div>
      </Box>

      <div className="flex mt-6">
        <Button
          variant="primary"
          type="button"
          disabled={!canUnregister}
          onClick={() => setShowPopup(true)}
        >
          Avanmäl gruppresa
        </Button>
      </div>

      {showPopup && (
        <Popup
          isOpen={showPopup}
          title="Bekräfta avanmälan"
          description="Vi vill bara dubbelkolla. Vill ni inte göra den här resan? När den är borttagen kan andra göra en anmälan till den. Ni är självklart välkomna att anmäla en ny gruppresa."
          onConfirm={handleConfirmUnregister}
          onCancel={() => setShowPopup(false)}
          cancelText="Avbryt"
          confirmText="Avanmäl gruppresa"
        />
      )}
    </div>
  );
}
